/**
 * O que faz: decisão de um retorno sugerido na revisão, com a data editável antes de aprovar.
 *            Começa na data da IA; a atendente ajusta pelo calendário/horário ou por atalhos (1 semana,
 *            15 dias, 1 mês, 2 meses) e agenda já com a data certa, sem precisar editar depois.
 * Onde usa: ModalRevisaoAgentes, na barra de decisão quando o item é um retorno.
 * Conexões: funções de data de revisao.ts; a gravação é do dono (RPC aprovar_sugestao_retorno com p_agendar_para).
 * Tabelas: nenhuma direta.
 * Data: 28/09/2026.
 */
import { useState } from 'react';
import { CalendarClock, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ItemRevisao } from '../contratos';
import { camposDataRetorno, dataDaquiADias, isoDataRetorno, validarDataRetorno } from '../revisao';

interface DecisaoRetornoRevisaoProps {
  item: ItemRevisao;
  ocupado: boolean;
  onAgendar: (agendarPara: string) => void;
  onRecusar: () => void;
}

// Atalhos mais pedidos quando o lead não dá data ("mês que vem", "daqui umas semanas").
const ATALHOS: Array<{ rotulo: string; dias: number }> = [
  { rotulo: '1 semana', dias: 7 },
  { rotulo: '15 dias', dias: 15 },
  { rotulo: '1 mês', dias: 30 },
  { rotulo: '2 meses', dias: 60 },
];

export function DecisaoRetornoRevisao({ item, ocupado, onAgendar, onRecusar }: DecisaoRetornoRevisaoProps) {
  // Estado local por item: o modal recria este componente a cada sugestão escolhida.
  const inicial = camposDataRetorno(item.agendarPara);
  const [data, setData] = useState(inicial.data);
  const [hora, setHora] = useState(inicial.hora);
  const iso = isoDataRetorno(data, hora);
  const erro = validarDataRetorno(iso);
  const alterada = data !== inicial.data || hora !== inicial.hora;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor={`retorno-data-${item.itemId}`} className="text-xs">Data do retorno</Label>
          <Input
            id={`retorno-data-${item.itemId}`}
            type="date"
            value={data}
            min={dataDaquiADias(0)}
            max={dataDaquiADias(365)}
            aria-invalid={Boolean(erro)}
            aria-describedby={`retorno-ajuda-${item.itemId}`}
            onChange={(evento) => setData(evento.target.value)}
            className="h-9 w-40 bg-card"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`retorno-hora-${item.itemId}`} className="text-xs">Horário</Label>
          <Input
            id={`retorno-hora-${item.itemId}`}
            type="time"
            value={hora}
            step={900}
            onChange={(evento) => setHora(evento.target.value)}
            className="h-9 w-28 bg-card"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Atalhos de data">
          {ATALHOS.map((atalho) => {
            const valor = dataDaquiADias(atalho.dias);
            return (
              <button
                key={atalho.dias}
                type="button"
                aria-pressed={data === valor}
                onClick={() => setData(valor)}
                className={cn(
                  'h-8 rounded-full border border-input px-3 text-xs transition-colors duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  data === valor && 'border-foreground bg-foreground text-background hover:bg-foreground',
                )}
              >
                {atalho.rotulo}
              </button>
            );
          })}
        </div>
      </div>

      {/* Ajuda muda com o estado: data da IA (estimada ou não), data ajustada ou erro de validação. */}
      <p
        id={`retorno-ajuda-${item.itemId}`}
        key={erro ?? (alterada ? 'alterada' : 'ia')}
        role={erro ? 'alert' : undefined}
        className={cn('text-xs animate-in fade-in-0 duration-200 motion-reduce:animate-none', erro ? 'lg-texto-perigo' : 'text-muted-foreground')}
      >
        {erro ?? (alterada
          ? 'Data ajustada por você. O retorno será agendado nesta data.'
          : item.dataEstimada
            ? 'O lead não disse uma data exata: a IA estimou esta. Ajuste se preferir.'
            : 'Data informada na conversa. Ajuste se precisar.')}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" disabled={ocupado || Boolean(erro) || !iso} onClick={() => iso && onAgendar(iso)}>
          {ocupado ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="mr-1 h-4 w-4" aria-hidden="true" />}
          Agendar retorno
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={ocupado} onClick={onRecusar}>
          <X className="mr-1 h-4 w-4" aria-hidden="true" />Recusar
        </Button>
        {alterada && !erro && inicial.data && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground animate-in fade-in-0 duration-200 motion-reduce:animate-none">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
            antes: {inicial.data.split('-').reverse().join('/')} às {inicial.hora}
          </span>
        )}
      </div>
    </div>
  );
}
